import { trackStructEvent } from "@snowplow/browser-tracker";
import { AxiosError } from "axios";
import React, { createContext, ReactNode, useContext, useEffect } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { FeatureFlagResponse } from "src/global";

import {
  fetchFeatureFlags,
  GET_FEATURE_FLAGS_METRIC_NAME,
} from "@/apis/FeatureFlagsHelper";
import { getRetryDelayMs, shouldRetry } from "@/helpers/react-query-helper";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

interface FeatureFlagsContextType {
  featureFlags: FeatureFlagResponse;
  isLoading: boolean;
  error: AxiosError | Error | null;
  isError: boolean;
  /** True once the query succeeded at least once, also while a later refetch fails. */
  hasLoaded: boolean;
}

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

/**
 * Custom hook to fetch feature flags and return them for conditional rendering
 * of experimental UI features.
 */

const useFeatureFlagsQuery = function (): UseQueryResult<
  FeatureFlagResponse,
  AxiosError | Error
> {
  const { state } = useContext(AppContext);

  return useQuery(GET_FEATURE_FLAGS_METRIC_NAME, fetchFeatureFlags, {
    retry: (failureCount, err) => shouldRetry(failureCount, err),
    retryDelay: (retryCount) => getRetryDelayMs(retryCount),
    enabled: state.amplifyConfigured,
  });
};

const defaultContextValue: FeatureFlagsContextType = {
  featureFlags: {},
  isLoading: true,
  error: null,
  isError: false,
  hasLoaded: false,
};

const FeatureFlagsContext =
  createContext<FeatureFlagsContextType>(defaultContextValue);

/**
 * Provider component that fetches feature flags once and provides them to all child components
 * through context, avoiding multiple API calls.
 */
export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const {
    data: featureFlags,
    isLoading,
    error,
    isError,
    dataUpdatedAt,
  }: UseQueryResult<
    FeatureFlagResponse,
    AxiosError | Error
  > = useFeatureFlagsQuery();

  const { dispatch } = useContext(AppContext);

  // When feature flags arrive, rebuild the step list so that flag-gated steps
  // are added to (or removed from) state.steps.
  useEffect(() => {
    if (featureFlags && !isLoading) {
      dispatch({
        type: ActionType.REBUILD_STEPS,
        featureFlags,
      });
    }
  }, [featureFlags, isLoading]);

  const contextValue: FeatureFlagsContextType = {
    featureFlags: featureFlags || {},
    isLoading,
    error,
    isError,
    hasLoaded: dataUpdatedAt > 0,
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to access feature flags from context
 * @returns FeatureFlagsContextType containing feature flags data and utilities
 */
export const useFeatureFlagsContext = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  return context;
};

// Track logged flags to prevent duplicate events - resets on page load
const loggedFlags = new Set<string>();

/**
 * All existing feature flags in EZO.
 *
 * Uses union with `(string & {})` to provide autocomplete for known flags while
 * allowing any string value for testing purposes.
 */
export type FeatureFlags =
  | "aiAutomation"
  | "ezoJourneyCapture"
  | "macVpnOrcaCorp"
  | "winVpnSkipPreferenceSeed"
  | "cameraResolution1080pCap"
  | "idStillCaptureEnabled"
  | "ivvPersonaTimeout"
  | "observeAsyncSummarizer"
  | "animatedGifGestures"
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/**
 * Hook to get a specific feature flag value from context
 * @param flagName - The name of the feature flag
 * @returns boolean indicating if the feature flag is enabled
 */
export const useFeatureFlag = (
  flagName: FeatureFlags,
  defaultValue = false
): boolean => {
  const { featureFlags, isLoading } = useFeatureFlagsContext();
  const flagValue = featureFlags?.[flagName]?.enabled ?? defaultValue;

  useEffect(() => {
    if (
      flagName &&
      !isLoading &&
      Object.keys(featureFlags).length > 0 &&
      !loggedFlags.has(flagName)
    ) {
      loggedFlags.add(flagName);
      trackStructEvent({
        category: Day1EventCategory.FeatureFlags,
        action: Day1EventAction.FeatureFlagAccessed,
        label: flagName,
        value: flagValue ? 1 : 0,
      });
    }
  }, [flagName, flagValue, isLoading, featureFlags]);

  return flagValue;
};
